import React, { useState, useEffect, useMemo, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { 
  Search, 
  DollarSign, 
  Building2, 
  FileText, 
  AlertCircle,
  Clock,
  Zap,
  CheckCircle2,
  Upload,
  Eye,
  X,
  ChevronRight,
  ChevronDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (val, decimals = 0) => {
  if (val === null || val === undefined) return '—';
  return val.toLocaleString('es-CL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

const ESTADO_CONFIG = {
  pendiente: { label: 'Pendiente', color: 'bg-sky-100 text-sky-700 border-sky-300 hover:bg-sky-200', icon: Clock },
  vencida: { label: 'Vencida', color: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200', icon: AlertCircle },
  pagada: { label: 'Pagada', color: 'bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200', icon: CheckCircle2 },
};

// Orden del ciclo de estados
const ESTADO_CYCLE = ['pendiente', 'vencida', 'pagada'];

const CuentasPorCobrar = () => {
  const { user } = useAuth();
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [loadingTCId, setLoadingTCId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedCuentaForUpload, setSelectedCuentaForUpload] = useState(null);
  
  // Estado para barra de progreso de TC masivo
  const [tcProgress, setTcProgress] = useState({ current: 0, total: 0 });
  
  // Estado para filas expandidas
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Estados para filtros avanzados
  const [filterEstados, setFilterEstados] = useState([]); // Array para multi-selección
  const [filterCliente, setFilterCliente] = useState('todos');
  const [filterCorredor, setFilterCorredor] = useState('todos');
  
  // Estado para ordenamiento
  const [sortConfig, setSortConfig] = useState({ key: 'fecha_max_descarga', direction: 'desc' });
  
  const toggleRowExpand = (cuentaId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cuentaId)) {
        newSet.delete(cuentaId);
      } else {
        newSet.add(cuentaId);
      }
      return newSet;
    });
  };
  
  // Función para cambiar ordenamiento
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  // Toggle estado en filtro multi-selección
  const toggleEstadoFilter = (estado) => {
    setFilterEstados(prev => {
      if (prev.includes(estado)) {
        return prev.filter(e => e !== estado);
      } else {
        return [...prev, estado];
      }
    });
  };
  
  // Obtener lista única de clientes para el filtro
  const clientesUnicos = useMemo(() => {
    const clientes = [...new Set(cuentas.map(c => c.cliente_alias).filter(Boolean))];
    return clientes.sort();
  }, [cuentas]);

  // Obtener lista única de corredores para el filtro
  const corredoresUnicos = useMemo(() => {
    const corredores = [...new Set(cuentas.map(c => c.broker).filter(Boolean))];
    return corredores.sort();
  }, [cuentas]);

  useEffect(() => {
    fetchCuentas();
  }, []);

  const fetchCuentas = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/cuentas-por-cobrar`);
      setCuentas(res.data);
    } catch (err) {
      toast.error('Error al cargar cuentas por cobrar');
    } finally {
      setLoading(false);
    }
  };

  // Ciclar estado de una cuenta (click para cambiar al siguiente)
  const ciclarEstado = async (cuenta) => {
    const currentEstado = cuenta.estado || 'pendiente';
    const currentIndex = ESTADO_CYCLE.indexOf(currentEstado);
    const nextEstado = ESTADO_CYCLE[(currentIndex + 1) % ESTADO_CYCLE.length];
    
    try {
      // Construir payload según tipo de agrupación
      const payload = { estado: nextEstado };
      if (cuenta.grupo_tipo === 'factura' && cuenta.factura) {
        payload.factura = cuenta.factura;
      } else {
        payload.dispatch_id = cuenta.dispatch_id;
        payload.client_id = cuenta.cliente_id;
      }
      
      await axios.put(`${API}/cuentas-por-cobrar/estado`, payload);
      
      // Actualizar localmente para respuesta inmediata
      setCuentas(prev => prev.map(c => 
        c.id === cuenta.id ? { ...c, estado: nextEstado } : c
      ));
      
      toast.success(`Estado: ${ESTADO_CONFIG[nextEstado].label}`);
    } catch (err) {
      toast.error('Error al cambiar estado');
    }
  };

  // Subir factura
  const handleUploadClick = (cuenta) => {
    setSelectedCuentaForUpload(cuenta);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCuentaForUpload) return;
    
    setUploadingId(selectedCuentaForUpload.id);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/cuentas-por-cobrar/factura/${selectedCuentaForUpload.dispatch_id}/${selectedCuentaForUpload.cliente_id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Factura subida correctamente');
      fetchCuentas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir factura');
    } finally {
      setUploadingId(null);
      setSelectedCuentaForUpload(null);
      e.target.value = '';
    }
  };

  // Ver factura
  const verFactura = (cuenta) => {
    if (cuenta.factura_url) {
      // Usar /api prefix para que pase por el backend
      const url = cuenta.factura_url.startsWith('/uploads/') 
        ? `${BASE_URL}/api${cuenta.factura_url}`
        : `${BASE_URL}${cuenta.factura_url}`;
      window.open(url, '_blank');
    }
  };

  // Eliminar factura
  const eliminarFactura = async (cuenta) => {
    if (!window.confirm('¿Eliminar la factura?')) return;
    
    try {
      await axios.delete(`${API}/cuentas-por-cobrar/factura/${cuenta.dispatch_id}/${cuenta.cliente_id}`);
      toast.success('Factura eliminada');
      fetchCuentas();
    } catch (err) {
      toast.error('Error al eliminar factura');
    }
  };

  // Obtener TC del Banco Central para TODAS las líneas (por fecha)
  const obtenerTCTodas = async () => {
    if (filteredCuentas.length === 0) {
      toast.info('No hay líneas para actualizar');
      return;
    }
    
    // Contar total de fechas a procesar
    let totalFechas = 0;
    for (const cuenta of filteredCuentas) {
      const detalles = cuenta.detalles_por_fecha || [];
      totalFechas += detalles.length || 1;
    }
    
    setLoadingTCId('all');
    setTcProgress({ current: 0, total: totalFechas });
    let updated = 0;
    let errors = 0;
    let processed = 0;
    
    // Procesar cada cuenta y cada fecha
    for (const cuenta of filteredCuentas) {
      const detalles = cuenta.detalles_por_fecha || [];
      
      if (detalles.length > 0) {
        // Procesar cada fecha individualmente
        for (const detalle of detalles) {
          processed++;
          setTcProgress({ current: processed, total: totalFechas });
          
          if (!detalle.fecha) {
            errors++;
            continue;
          }
          
          try {
            // 1. Obtener TC del Banco Central para esta fecha
            const res = await axios.get(`${API}/cuentas-por-cobrar/tc-cmf/${detalle.fecha}`);
            const tcValor = res.data.tipo_cambio;
            
            // 2. Guardar TC con la fecha específica (según tipo de agrupación)
            const payload = {
              fecha: detalle.fecha,
              tipo_cambio: tcValor
            };
            if (cuenta.grupo_tipo === 'factura' && cuenta.factura) {
              payload.factura = cuenta.factura;
            } else {
              payload.dispatch_id = cuenta.dispatch_id;
              payload.client_id = cuenta.cliente_id;
            }
            
            await axios.put(`${API}/cuentas-por-cobrar/tipo-cambio`, payload);
            
            updated++;
          } catch (err) {
            console.error('Error TC:', cuenta.id, detalle.fecha, err);
            errors++;
          }
        }
      } else {
        // Fallback: si no hay detalles, usar fecha_max_descarga
        processed++;
        setTcProgress({ current: processed, total: totalFechas });
        
        const fecha = cuenta.fecha_max_descarga;
        if (!fecha) {
          errors++;
          continue;
        }
        
        try {
          const res = await axios.get(`${API}/cuentas-por-cobrar/tc-cmf/${fecha}`);
          const tcValor = res.data.tipo_cambio;
          
          const payload = {
            fecha: fecha,
            tipo_cambio: tcValor
          };
          if (cuenta.grupo_tipo === 'factura' && cuenta.factura) {
            payload.factura = cuenta.factura;
          } else {
            payload.dispatch_id = cuenta.dispatch_id;
            payload.client_id = cuenta.cliente_id;
          }
          
          await axios.put(`${API}/cuentas-por-cobrar/tipo-cambio`, payload);
          
          updated++;
        } catch (err) {
          console.error('Error TC:', cuenta.id, fecha, err);
          errors++;
        }
      }
    }
    
    // Siempre refrescar desde el servidor para garantizar sincronización
    await fetchCuentas();
    
    setLoadingTCId(null);
    setTcProgress({ current: 0, total: 0 });
    
    if (updated > 0) {
      toast.success(`TC actualizado para ${updated} fechas` + (errors > 0 ? ` (${errors} errores)` : ''));
    } else if (errors > 0) {
      toast.error(`No se pudo actualizar ninguna fecha (${errors} errores)`);
    }
  };

  const filteredCuentas = useMemo(() => {
    const term = filterText.toLowerCase();
    
    // Primero filtrar
    let filtered = cuentas.filter(c => {
      // Filtro de texto
      const matchText = !term || 
        c.dispatch_number?.toLowerCase().includes(term) ||
        c.cliente_alias?.toLowerCase().includes(term) ||
        c.cierre?.toLowerCase().includes(term) ||
        c.factura?.toLowerCase().includes(term) ||
        c.broker?.toLowerCase().includes(term) ||
        c.fecha_max_descarga?.includes(term);
      
      // Filtro de estado (multi-selección)
      const matchEstado = filterEstados.length === 0 || filterEstados.includes(c.estado);
      
      // Filtro de cliente
      const matchCliente = filterCliente === 'todos' || c.cliente_alias === filterCliente;
      
      // Filtro de corredor
      const matchCorredor = filterCorredor === 'todos' || c.broker === filterCorredor;
      
      return matchText && matchEstado && matchCliente && matchCorredor;
    });
    
    // Luego ordenar
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Manejar valores nulos
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';
        
        // Ordenar strings
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [cuentas, filterText, filterEstados, filterCliente, filterCorredor, sortConfig]);

  // Calcular totales
  const totales = useMemo(() => {
    return filteredCuentas.reduce((acc, c) => ({
      monto_usd_neto: acc.monto_usd_neto + (c.monto_usd_neto || 0),
      monto_pesos_neto: acc.monto_pesos_neto + (c.monto_pesos_neto || 0),
      iva_pesos: acc.iva_pesos + (c.iva_pesos || 0),
      bruto_pesos: acc.bruto_pesos + (c.bruto_pesos || 0),
    }), { monto_usd_neto: 0, monto_pesos_neto: 0, iva_pesos: 0, bruto_pesos: 0 });
  }, [filteredCuentas]);

  // Estadísticas
  const stats = useMemo(() => {
    // Contar fechas sin TC (no líneas sin TC)
    let sinTC = 0;
    let conTC = 0;
    
    filteredCuentas.forEach(c => {
      const detalles = c.detalles_por_fecha || [];
      if (detalles.length > 0) {
        detalles.forEach(d => {
          if (d.tipo_cambio) conTC++;
          else sinTC++;
        });
      } else {
        // Fallback para cuentas sin detalles
        sinTC++;
      }
    });
    
    return { total: filteredCuentas.length, sinTC, conTC };
  }, [filteredCuentas]);

  if (user?.role !== 'MASTER') {
    return (
      <Layout>
        <div className="text-center py-10">
          <p className="text-slate-500">No tienes permiso para acceder a este módulo</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="cuentas-por-cobrar-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Cuentas por Cobrar</h1>
            <p className="text-slate-600 mt-1">Gestión de facturas pendientes de cobro</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <FileText size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-blue-600 font-medium">Total Líneas</p>
                <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500 rounded-lg">
                <DollarSign size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Total USD Neto</p>
                <p className="text-2xl font-bold text-emerald-700">$ {fmtMoney(totales.monto_usd_neto, 2)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <AlertCircle size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-amber-600 font-medium">Sin TC</p>
                <p className="text-2xl font-bold text-amber-700">{stats.sinTC}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500 rounded-lg">
                <CheckCircle2 size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-violet-600 font-medium">Total Bruto CLP</p>
                <p className="text-2xl font-bold text-violet-700">$ {fmtMoney(totales.bruto_pesos)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filtros */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-4 items-end">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold text-slate-500 uppercase">Búsqueda</span>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input 
                placeholder="Despacho, cliente, cierre, factura, corredor..." 
                value={filterText} 
                onChange={e => setFilterText(e.target.value)} 
                className="pl-8 h-9 text-sm w-64"
                data-testid="search-input"
              />
            </div>
          </div>
          
          {/* Filtro de Estados (multi-selección con botones) */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold text-slate-500 uppercase">Estados</span>
            <div className="flex gap-1">
              <button
                onClick={() => toggleEstadoFilter('pendiente')}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                  filterEstados.includes('pendiente') 
                    ? 'bg-sky-100 text-sky-700 border-sky-300' 
                    : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid="filter-estado-pendiente"
              >
                Pendiente
              </button>
              <button
                onClick={() => toggleEstadoFilter('vencida')}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                  filterEstados.includes('vencida') 
                    ? 'bg-red-100 text-red-700 border-red-300' 
                    : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid="filter-estado-vencida"
              >
                Vencida
              </button>
              <button
                onClick={() => toggleEstadoFilter('pagada')}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                  filterEstados.includes('pagada') 
                    ? 'bg-emerald-100 text-emerald-700 border-emerald-300' 
                    : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid="filter-estado-pagada"
              >
                Pagada
              </button>
            </div>
          </div>
          
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold text-slate-500 uppercase">Cliente</span>
            <select
              value={filterCliente}
              onChange={e => setFilterCliente(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[180px]"
              data-testid="filter-cliente"
            >
              <option value="todos">Todos los clientes</option>
              {clientesUnicos.map(cliente => (
                <option key={cliente} value={cliente}>{cliente}</option>
              ))}
            </select>
          </div>
          
          {/* Filtro de Corredor */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold text-slate-500 uppercase">Corredor</span>
            <select
              value={filterCorredor}
              onChange={e => setFilterCorredor(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[150px]"
              data-testid="filter-corredor"
            >
              <option value="todos">Todos</option>
              {corredoresUnicos.map(corredor => (
                <option key={corredor} value={corredor}>{corredor}</option>
              ))}
            </select>
          </div>
          
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold text-slate-500 uppercase">Ordenar por</span>
            <select
              value={`${sortConfig.key}_${sortConfig.direction}`}
              onChange={e => {
                const [key, dir] = e.target.value.split('_');
                setSortConfig({ key, direction: dir });
              }}
              className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="sort-select"
            >
              <option value="fecha_max_descarga_desc">Fecha ↓ (Reciente)</option>
              <option value="fecha_max_descarga_asc">Fecha ↑ (Antigua)</option>
              <option value="fecha_vencimiento_asc">Vencimiento ↑ (Próximo)</option>
              <option value="fecha_vencimiento_desc">Vencimiento ↓ (Lejano)</option>
              <option value="dispatch_number_asc">Despacho A-Z</option>
              <option value="dispatch_number_desc">Despacho Z-A</option>
              <option value="cliente_alias_asc">Cliente A-Z</option>
              <option value="cliente_alias_desc">Cliente Z-A</option>
              <option value="bruto_pesos_desc">Monto ↓ (Mayor)</option>
              <option value="bruto_pesos_asc">Monto ↑ (Menor)</option>
            </select>
          </div>
          
          {/* Botón para limpiar filtros */}
          {(filterText || filterEstados.length > 0 || filterCliente !== 'todos' || filterCorredor !== 'todos') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilterText('');
                setFilterEstados([]);
                setFilterCliente('todos');
                setFilterCorredor('todos');
              }}
              className="text-slate-500 hover:text-slate-700"
            >
              <X size={14} className="mr-1" />
              Limpiar
            </Button>
          )}

          <span className="text-xs text-slate-400 ml-auto">
            {filteredCuentas.length} de {cuentas.length} líneas
          </span>
        </div>

        {/* Barra de progreso para TC masivo */}
        {loadingTCId === 'all' && tcProgress.total > 0 && (
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4" data-testid="tc-progress-bar">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Zap size={16} className="text-violet-600 animate-pulse" />
                <span className="text-sm font-medium text-violet-700">
                  Obteniendo Tipo de Cambio del Banco Central...
                </span>
              </div>
              <span className="text-sm font-mono font-semibold text-violet-600">
                {tcProgress.current} / {tcProgress.total}
              </span>
            </div>
            <div className="w-full bg-violet-200 rounded-full h-2.5 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-violet-500 to-violet-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${(tcProgress.current / tcProgress.total) * 100}%` }}
              />
            </div>
            <p className="text-xs text-violet-500 mt-2">
              Consultando mindicador.cl (BCCH) para cada fecha de descarga...
            </p>
          </div>
        )}

        {/* Tabla */}
        <Card className="border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-auto max-h-[60vh]">
            <table className="w-full text-sm" data-testid="cuentas-table">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr className="border-b border-slate-300">
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase">N° Despacho</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase">Cliente</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase">Corredor</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase">N° Cierre</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase">N° Factura</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase">Fecha</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-bold text-slate-500 uppercase">Cam.</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-bold text-slate-500 uppercase">Tons</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-bold text-slate-500 uppercase">USD Neto</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-bold text-slate-500 uppercase w-24">
                    <div className="flex items-center justify-center gap-1.5">
                      <span>TC</span>
                      <button
                        onClick={obtenerTCTodas}
                        disabled={loadingTCId === 'all'}
                        className="text-violet-500 hover:text-violet-700 disabled:text-violet-300 transition-colors"
                        title="Obtener TC BCCH para todas las líneas"
                        data-testid="get-tc-bcch-all"
                      >
                        <Zap size={13} className={loadingTCId === 'all' ? 'animate-pulse' : ''} />
                      </button>
                    </div>
                  </th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-bold text-slate-500 uppercase">Bruto</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase">Vencimiento</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-bold text-slate-500 uppercase">Estado</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-bold text-slate-500 uppercase">Factura</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredCuentas.map(cuenta => {
                  const estadoConfig = ESTADO_CONFIG[cuenta.estado] || ESTADO_CONFIG.pendiente;
                  const detalles = cuenta.detalles_por_fecha || [];
                  const tieneMultiplesFechas = detalles.length > 1;
                  const isExpanded = expandedRows.has(cuenta.id);
                  
                  // Obtener datos de la fecha más tardía para mostrar cuando está colapsado
                  const fechaMasTardia = detalles.length > 0 ? detalles[detalles.length - 1] : null;
                  
                  return (
                    <React.Fragment key={cuenta.id}>
                      {/* Fila principal (siempre visible) */}
                      <tr 
                        className={`hover:bg-slate-50 ${cuenta.estado === 'pagada' ? 'bg-emerald-50/30' : cuenta.estado === 'vencida' ? 'bg-red-50/30' : ''}`}
                        data-testid={`cuenta-row-${cuenta.id}`}
                      >
                        {/* N° Despacho con botón expandir si tiene múltiples fechas */}
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            {tieneMultiplesFechas && (
                              <button
                                onClick={() => toggleRowExpand(cuenta.id)}
                                className="p-0.5 hover:bg-slate-200 rounded transition-colors"
                                title={isExpanded ? 'Colapsar' : 'Expandir'}
                              >
                                {isExpanded ? (
                                  <ChevronDown size={14} className="text-slate-500" />
                                ) : (
                                  <ChevronRight size={14} className="text-slate-500" />
                                )}
                              </button>
                            )}
                            {/* Mostrar despacho(s) - puede ser múltiple si agrupado por factura */}
                            <span className="font-mono text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" title={cuenta.dispatch_number}>
                              {cuenta.dispatch_numbers && cuenta.dispatch_numbers.length > 1 
                                ? `${cuenta.dispatch_numbers.length} desps.` 
                                : cuenta.dispatch_number}
                            </span>
                            {tieneMultiplesFechas && !isExpanded && (
                              <span className="text-[9px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded">
                                {detalles.length} fechas
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <Building2 size={12} className="text-slate-400" />
                            <span className="text-xs font-medium text-slate-700 truncate max-w-[100px]" title={cuenta.cliente_alias}>
                              {cuenta.cliente_alias}
                            </span>
                          </div>
                        </td>
                        {/* Corredor */}
                        <td className="px-3 py-2">
                          <span className="text-xs text-slate-600 truncate max-w-[80px]" title={cuenta.broker}>
                            {cuenta.broker || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-xs font-mono text-slate-600">{cuenta.cierre || '—'}</span>
                        </td>
                        {/* N° Factura - resaltado si es la clave de agrupación */}
                        <td className="px-3 py-2">
                          {cuenta.grupo_tipo === 'factura' ? (
                            <span className="text-xs font-mono font-bold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded" title="Agrupado por factura">
                              {cuenta.factura}
                            </span>
                          ) : (
                            <span className="text-xs font-mono text-slate-600">{cuenta.factura || '—'}</span>
                          )}
                        </td>
                        {/* Fecha: mostrar la más tardía cuando está colapsado */}
                        <td className="px-3 py-2">
                          <span className="text-xs text-slate-600 font-mono">
                            {fechaMasTardia ? fmtDate(fechaMasTardia.fecha) : fmtDate(cuenta.fecha_max_descarga)}
                          </span>
                        </td>
                        {/* Cam: total cuando colapsado */}
                        <td className="px-3 py-2 text-center">
                          <span className="text-xs font-mono px-1.5 py-0.5 bg-slate-100 rounded text-slate-600">
                            {cuenta.cantidad_camiones || 1}
                          </span>
                        </td>
                        {/* Tons: total cuando colapsado */}
                        <td className="px-3 py-2 text-right">
                          <span className="text-xs font-mono text-slate-600">
                            {cuenta.toneladas?.toFixed(1) || '—'}
                          </span>
                        </td>
                        {/* USD: total cuando colapsado */}
                        <td className="px-3 py-2 text-right">
                          <span className="text-xs font-mono font-semibold text-emerald-700">
                            $ {fmtMoney(cuenta.monto_usd_neto, 2)}
                          </span>
                        </td>
                        {/* TC: de la fecha más tardía cuando colapsado */}
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center">
                            <span className={`text-xs font-mono tabular-nums ${fechaMasTardia?.tipo_cambio ? 'text-slate-700' : 'text-amber-500'}`}>
                              {fechaMasTardia?.tipo_cambio ? fechaMasTardia.tipo_cambio.toFixed(2) : '—'}
                            </span>
                          </div>
                        </td>
                        {/* Bruto: total cuando colapsado */}
                        <td className="px-3 py-2 text-right">
                          <span className={`text-xs font-mono font-semibold ${cuenta.bruto_pesos ? 'text-violet-700' : 'text-slate-300'}`}>
                            {cuenta.bruto_pesos ? `$ ${fmtMoney(cuenta.bruto_pesos)}` : '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {cuenta.fecha_vencimiento ? (
                            <span className={`text-xs font-mono ${
                              cuenta.estado === 'vencida' ? 'text-red-600' : 'text-slate-600'
                            }`}>
                              {fmtDate(cuenta.fecha_vencimiento)}
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {(() => {
                            const EstadoIcon = estadoConfig.icon;
                            return (
                              <button
                                onClick={() => ciclarEstado(cuenta)}
                                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded border cursor-pointer transition-all ${estadoConfig.color}`}
                                data-testid={`estado-btn-${cuenta.id}`}
                                title="Click para cambiar estado"
                              >
                                <EstadoIcon size={12} />
                                {estadoConfig.label}
                              </button>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1">
                            {cuenta.factura_url ? (
                              <>
                                <button
                                  onClick={() => verFactura(cuenta)}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Ver factura"
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  onClick={() => eliminarFactura(cuenta)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                  title="Eliminar factura"
                                >
                                  <X size={14} />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => handleUploadClick(cuenta)}
                                disabled={uploadingId === cuenta.id}
                                className="p-1 text-slate-500 hover:bg-slate-100 rounded disabled:opacity-50"
                                title="Subir factura"
                              >
                                <Upload size={14} className={uploadingId === cuenta.id ? 'animate-pulse' : ''} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* Filas expandidas (detalles por fecha) */}
                      {isExpanded && detalles.map((detalle, idx) => (
                        <tr 
                          key={`${cuenta.id}-detail-${detalle.fecha}`}
                          className="bg-slate-50/50 border-t border-dashed border-slate-200"
                          data-testid={`cuenta-detail-${cuenta.id}-${idx}`}
                        >
                          <td className="px-3 py-1.5 pl-8">
                            <span className="text-[10px] text-slate-400">↳</span>
                          </td>
                          <td className="px-3 py-1.5"></td>
                          {/* Corredor - vacío en sub-filas */}
                          <td className="px-3 py-1.5"></td>
                          {/* N° Cierre de esta fecha si existe */}
                          <td className="px-3 py-1.5">
                            {detalle.cierres && detalle.cierres.length > 0 ? (
                              <span className="text-[10px] font-mono text-slate-500">
                                {detalle.cierres.join(', ')}
                              </span>
                            ) : null}
                          </td>
                          {/* N° Factura - vacío en sub-filas */}
                          <td className="px-3 py-1.5"></td>
                          <td className="px-3 py-1.5">
                            <span className="text-xs text-slate-600 font-mono">{fmtDate(detalle.fecha)}</span>
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            <span className="text-xs font-mono text-slate-500">
                              {detalle.cantidad_camiones}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <span className="text-xs font-mono text-slate-500">
                              {detalle.toneladas?.toFixed(1)}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <span className="text-xs font-mono text-emerald-600">
                              $ {fmtMoney(detalle.monto_usd, 2)}
                            </span>
                          </td>
                          <td className="px-3 py-1.5">
                            <div className="flex items-center justify-center">
                              <span className={`text-xs font-mono tabular-nums ${detalle.tipo_cambio ? 'text-slate-600' : 'text-amber-500'}`}>
                                {detalle.tipo_cambio ? detalle.tipo_cambio.toFixed(2) : '—'}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <span className={`text-xs font-mono ${detalle.bruto_pesos ? 'text-violet-600' : 'text-slate-300'}`}>
                              {detalle.bruto_pesos ? `$ ${fmtMoney(detalle.bruto_pesos)}` : '—'}
                            </span>
                          </td>
                          <td colSpan={3}></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                {filteredCuentas.length === 0 && (
                  <tr>
                    <td colSpan={14} className="px-4 py-8 text-center text-slate-400">
                      {loading ? 'Cargando...' : 'No hay cuentas por cobrar'}
                    </td>
                  </tr>
                )}
              </tbody>
              {filteredCuentas.length > 0 && (
                <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                  <tr>
                    <td colSpan={6} className="px-3 py-2.5 text-right text-xs font-bold text-slate-600 uppercase">
                      Totales
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="text-sm font-mono font-bold text-slate-600">
                        {filteredCuentas.reduce((acc, c) => acc + (c.toneladas || 0), 0).toFixed(1)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="text-sm font-mono font-bold text-emerald-700">
                        $ {fmtMoney(totales.monto_usd_neto, 2)}
                      </span>
                    </td>
                    <td></td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="text-sm font-mono font-bold text-violet-700">
                        $ {fmtMoney(totales.bruto_pesos)}
                      </span>
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>

        {/* Leyenda */}
        <div className="flex items-center gap-6 text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-sky-100 border border-sky-300"></div>
            <span>Pendiente</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-red-100 border border-red-300"></div>
            <span>Vencida</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></div>
            <span>Pagada</span>
          </div>
          <span className="text-slate-400 ml-2">• Click en estado para cambiar</span>
        </div>

        {/* Input oculto para subir archivos */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,.png,.jpg,.jpeg"
          className="hidden"
          data-testid="file-input-factura"
        />
      </div>
    </Layout>
  );
};

export default CuentasPorCobrar;
